import {useCallback, useEffect, useMemo, useState} from 'react';
import {LocalStorage} from '../../utils/LocalStorage';

export const STEP_CONNECT_DATA_SOURCES = 'connect_data_sources';
export const STEP_FIRST_CONTROL_LINE = 'first_control_line';
export const STEP_SELECT_DASHBOARD = 'select_dashboard';
export const STEP_FIRST_ALERT = 'first_alert';
export const STEP_DATA_EXPLORER_REPORT = 'data_explorer_report';

export const HELP_STEPS = [
    {key: STEP_CONNECT_DATA_SOURCES, type: 'manual', labelKey: 'help.step_connect_sources', shortLabelKey: 'help.step_short_connect_sources', descriptionKey: 'help.step_connect_sources_description', sectionKey: 'help.section_setup', powerUpsTotal: 1, powerUpsActive: 1, hasPreview: true},
    {key: STEP_FIRST_CONTROL_LINE, type: 'manual', labelKey: 'help.step_first_control_line', shortLabelKey: 'help.step_short_first_control_line', descriptionKey: 'help.step_first_control_line_description', sectionKey: 'help.section_dashboards'},
    {key: STEP_SELECT_DASHBOARD, type: 'manual', labelKey: 'help.step_select_dashboard', shortLabelKey: 'help.step_short_select_dashboard', descriptionKey: 'help.step_select_dashboard_description', powerUpsTotal: 3, powerUpsActive: 0},
    {key: STEP_FIRST_ALERT, type: 'manual', labelKey: 'help.step_first_alert', shortLabelKey: 'help.step_short_first_alert', descriptionKey: 'help.step_first_alert_description', sectionKey: 'help.section_alerts'},
    {key: STEP_DATA_EXPLORER_REPORT, type: 'manual', labelKey: 'help.step_data_explorer_report', shortLabelKey: 'help.step_short_data_explorer_report', descriptionKey: 'help.step_data_explorer_report_description', sectionKey: 'help.section_data_explorer', powerUpsTotal: 1, powerUpsActive: 0},
];

const DEFAULT_COMPLETED_STEPS = [STEP_CONNECT_DATA_SOURCES, STEP_FIRST_CONTROL_LINE];
const MANUAL_STORAGE_PREFIX = 'dashbo_help_manual_steps_v2_';

function getStorageKey(prefix, userId) {
    const suffix = userId || 'anon';
    return `${prefix}${suffix}`;
}

function readSet(prefix, userId, defaultItems = []) {
    try {
        const raw = localStorage.getItem(getStorageKey(prefix, userId));
        if (!raw) return new Set(defaultItems);
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? new Set(parsed) : new Set(defaultItems);
    } catch (e) {
        return new Set(defaultItems);
    }
}

function writeSet(prefix, userId, set) {
    try {
        localStorage.setItem(getStorageKey(prefix, userId), JSON.stringify(Array.from(set)));
    } catch (e) { /* ignore */ }
}

export function useOnboardingProgress({enabled = true} = {}) {
    const userId = LocalStorage.fetchUserId();

    const [manualSet, setManualSet] = useState(() => readSet(MANUAL_STORAGE_PREFIX, userId, DEFAULT_COMPLETED_STEPS));

    const toggleManualStep = useCallback((stepKey) => {
        setManualSet(prev => {
            const next = new Set(prev);
            if (next.has(stepKey)) {
                next.delete(stepKey);
            } else {
                next.add(stepKey);
            }
            writeSet(MANUAL_STORAGE_PREFIX, userId, next);
            return next;
        });
    }, [userId]);

    useEffect(() => {
        if (!enabled) return;
        if (!userId) return;
        setManualSet(readSet(MANUAL_STORAGE_PREFIX, userId, DEFAULT_COMPLETED_STEPS));
    }, [enabled, userId]);

    const steps = useMemo(() => HELP_STEPS.map(step => ({
        ...step,
        completed: manualSet.has(step.key),
    })), [manualSet]);

    const completedCount = steps.filter(s => s.completed).length;
    const totalCount = steps.length;
    const allCompleted = completedCount >= totalCount;
    const percent = totalCount === 0 ? 0 : Math.round((completedCount / totalCount) * 100);

    return {
        steps,
        completedCount,
        totalCount,
        percent,
        allCompleted,
        isChecking: false,
        toggleManualStep,
        refreshAutoSteps: () => {},
    };
}
